import Vue from 'vue';
import { mapState } from 'vuex';
import { db, QueryDocumentSnapshot } from '@/firebase';
import {
	Runnit,
	RunnitNode,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RUNNIT_NODE_RUN_STATE,
} from '@run-diffusion/shared';

export interface AvailableResultItem {
	nodeRunId: string
	nodeRun: RunnitNodeRun
	result: RunnitNodeRunResult
}

export interface LoadAvailableResultsResponse {
	results: AvailableResultItem[]
	canLoadMore: boolean
	lastDoc: QueryDocumentSnapshot | null
	isSourceRunnit: boolean
}

// Template generation constants
export const TEMPLATE_MAX_RESULTS_PER_NODE: number = 20;
const RESULTS_PER_PAGE = 50;

export const TemplateGenerationResultsMixin = Vue.extend({
	computed: {
		...mapState(['user']),
	},
	methods: {
		/**
		 * Loads available results for a given node in a template context.
		 * First tries to load from the source runnit (if provided), then falls back to user's own generations.
		 * Filters out template-related nodeRuns and deleted results.
		 */
		async loadAvailableResultsForNode (
			node: RunnitNode,
			sourceRunnit: Runnit | null = null,
			lastDoc: QueryDocumentSnapshot | null = null,
		): Promise<LoadAvailableResultsResponse> {
			let nodeRuns: RunnitNodeRun[] = [];
			let lastDocSnapshot: QueryDocumentSnapshot | null = null;
			let isSourceRunnit = false;
			let canLoadMore = false;

			// Primary source: Query source board nodeRuns directly
			if (sourceRunnit && (node as any).originalNodeId) {
				let sourceNodeRunsRef = db
					.collection('runnitNodeRuns')
					.where('runnitId', '==', sourceRunnit.id)
					.where('nodeId', '==', (node as any).originalNodeId)
					.where('state', '==', RUNNIT_NODE_RUN_STATE.DONE)
					.where('deletedAt', '==', null)
					.orderBy('initAt', 'desc')
					.limit(RESULTS_PER_PAGE);

				if (lastDoc) {
					sourceNodeRunsRef = sourceNodeRunsRef.startAfter(lastDoc);
				}

				const sourceNodeRunsSnapshot = await sourceNodeRunsRef.get();

				if (!sourceNodeRunsSnapshot.empty) {
					nodeRuns = sourceNodeRunsSnapshot.docs.map(doc => ({
						id: doc.id,
						...doc.data(),
					})) as RunnitNodeRun[];

					canLoadMore = sourceNodeRunsSnapshot.docs.length === RESULTS_PER_PAGE;
					lastDocSnapshot = sourceNodeRunsSnapshot.docs[sourceNodeRunsSnapshot.docs.length - 1];
					isSourceRunnit = true;
				}
			}

			// Fallback source: If no results from source board, query user's own generations
			if (nodeRuns.length === 0 && this.user) {
				let userNodeRunsRef = db
					.collection('runnitNodeRuns')
					.where('userId', '==', this.user.id)
					.where('nodeDefId', '==', node.nodeDefId)
					.where('state', '==', RUNNIT_NODE_RUN_STATE.DONE)
					.where('deletedAt', '==', null)
					.where('templateOriginalData', '==', null) // Filter out template clones
					.orderBy('initAt', 'desc')
					.limit(RESULTS_PER_PAGE);

				if (lastDoc) {
					userNodeRunsRef = userNodeRunsRef.startAfter(lastDoc);
				}

				const userNodeRunsSnapshot = await userNodeRunsRef.get();

				if (!userNodeRunsSnapshot.empty) {
					nodeRuns = userNodeRunsSnapshot.docs
						.map(doc => ({
							id: doc.id,
							...doc.data(),
						})) as RunnitNodeRun[];

					canLoadMore = userNodeRunsSnapshot.docs.length === RESULTS_PER_PAGE;
					lastDocSnapshot = userNodeRunsSnapshot.docs[userNodeRunsSnapshot.docs.length - 1];
					isSourceRunnit = false;
				}
			}

			// Flatten results from all nodeRuns
			const availableResults: AvailableResultItem[] = [];
			for (const nodeRun of nodeRuns) {
				if (nodeRun.results && nodeRun.results.length) {
					for (const result of nodeRun.results) {
						// Filter out deleted results
						if (!result.deletedAt) {
							availableResults.push({
								nodeRunId: nodeRun.id,
								nodeRun,
								result,
							});
						}
					}
				}
			}

			return {
				results: availableResults,
				canLoadMore,
				lastDoc: lastDocSnapshot,
				isSourceRunnit,
			};
		},
	},
});

